import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api, { apiErrorMessage } from '../services/api';
import FloatingInput from '../components/ui/FloatingInput';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/auth/reset-password', { token, password });
      toast.success('Password updated — you can sign in now.');
      navigate('/login');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not reset your password. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  // A missing token means the link was mangled or typed by hand; say so rather
  // than letting the user fill in the form and fail at submit.
  if (!token) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-soft-md border border-surface-100 p-7 text-center">
          <h1 className="text-h2 text-surface-900 mb-2">This link isn&apos;t valid</h1>
          <p className="text-body text-surface-600 mb-6">
            The reset link looks incomplete. Request a new one and use the most recent email.
          </p>
          <Link
            to="/forgot-password"
            className="block w-full text-center px-5 py-3 bg-primary-500 text-white text-body font-semibold rounded-xl hover:bg-primary-600 transition-colors duration-200"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-soft-md border border-surface-100 p-7">
          <h1 className="text-h2 text-surface-900 mb-2">Choose a new password</h1>
          <p className="text-body text-surface-600 mb-6">
            Must be at least 8 characters and include an uppercase letter, a lowercase letter, a
            number and a special character.
          </p>

          {error && (
            <div className="mb-4 rounded-xl bg-error-50 border border-error-500/20 px-4 py-3">
              <p className="text-body-sm text-error-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <FloatingInput
              id="password"
              name="password"
              type="password"
              label="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <FloatingInput
              id="confirm"
              name="confirm"
              type="password"
              label="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-5 py-3 bg-primary-500 text-white text-body font-semibold rounded-xl hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </form>

          <p className="text-body-sm text-surface-500 mt-6 text-center">
            <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
              Back to sign in
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}

export default ResetPassword;
